"use client";

import { c as _c } from "react/compiler-runtime";
import type { Media, Product, Variant } from "@spree/sdk";
import { CircleCheckBig, CircleX, Loader2, ShoppingBag } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { MediaGallery } from "@/components/products/MediaGallery";
import { ProductCustomFields } from "@/components/products/ProductCustomFields";
import { VariantPicker } from "@/components/products/VariantPicker";
import { Button } from "@/components/ui/button";
import { QuantityPicker } from "@/components/ui/quantity-picker";
import { SpreeRichText } from "@/components/ui/spree-rich-text";
import { useCart } from "@/contexts/CartContext";
import { useStore } from "@/contexts/StoreContext";
import { trackAddToCart, trackViewItem } from "@/lib/analytics/gtm";
import { isUnavailableInMarket } from "@/lib/market-restrictions";
import { salePricing } from "@/lib/utils/pricing";
interface ProductDetailsProps {
  product: Product;
  basePath: string;
}
export function ProductDetails(t0) {
  const $ = _c(82);
  const {
    product
  } = t0;
  const {
    addItem
  } = useCart();
  const {
    currency,
    market
  } = useStore();
  const t = useTranslations("products");
  const t1 = market?.name;
  let t2;
  if ($[0] !== product.slug || $[1] !== t1) {
    t2 = isUnavailableInMarket(product.slug, t1);
    $[0] = product.slug;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const unavailableInMarket = t2;
  let t3;
  if ($[3] !== product.variants) {
    t3 = product.variants || [];
    $[3] = product.variants;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = t3.filter(Boolean);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const variants = t4;
  const hasVariants = variants.length > 0;
  let t5;
  if ($[7] !== product.option_types) {
    t5 = product.option_types || [];
    $[7] = product.option_types;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const optionTypes = t5;
  let t6;
  if ($[9] !== hasVariants || $[10] !== product.default_variant || $[11] !== variants) {
    t6 = () => {
      if (product.default_variant) {
        return product.default_variant;
      }
      if (hasVariants) {
        return variants.find(_temp) || variants[0];
      }
      return product.default_variant || null;
    };
    $[9] = hasVariants;
    $[10] = product.default_variant;
    $[11] = variants;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const [selectedVariant, setSelectedVariant] = useState(t6);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  let t7;
  let t8;
  if ($[13] !== currency || $[14] !== product) {
    t7 = () => {
      trackViewItem(product, currency);
    };
    t8 = [product, currency];
    $[13] = currency;
    $[14] = product;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== product.media) {
    t9 = product.media || [];
    $[17] = product.media;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const galleryImages = t9;
  let t10;
  bb0: {
    if (!selectedVariant) {
      t10 = null;
      break bb0;
    }
    let t11;
    if ($[19] !== selectedVariant) {
      t11 = m => m.variant_ids.includes(selectedVariant.id);
      $[19] = selectedVariant;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    const index = galleryImages.findIndex(t11);
    t10 = index >= 0 ? index : null;
  }
  const variantImageIndex = t10;
  const price = selectedVariant?.price ?? product.price;
  const originalPrice = selectedVariant?.original_price ?? product.original_price;
  const locale = useLocale();
  let t11;
  if ($[21] !== locale || $[22] !== originalPrice || $[23] !== price) {
    t11 = salePricing(price, originalPrice, locale);
    $[21] = locale;
    $[22] = originalPrice;
    $[23] = price;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const {
    price: displayPrice,
    strikethrough: strikethroughPrice
  } = t11;
  const onSale = strikethroughPrice != null;
  const isPurchasable = hasVariants ? selectedVariant?.purchasable ?? false : product.purchasable ?? false;
  const inStock = hasVariants ? selectedVariant?.in_stock ?? false : product.in_stock ?? false;
  let t12;
  if ($[25] !== addItem || $[26] !== currency || $[27] !== product || $[28] !== quantity || $[29] !== selectedVariant) {
    t12 = async () => {
      const variantId = selectedVariant?.id || product.default_variant?.id || product.default_variant_id;
      if (!variantId) {
        throw new Error("No variant selected");
      }
      setLoading(true);
      await addItem(variantId, quantity);
      setLoading(false);
      trackAddToCart(product, selectedVariant, quantity, currency);
    };
    $[25] = addItem;
    $[26] = currency;
    $[27] = product;
    $[28] = quantity;
    $[29] = selectedVariant;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const handleAddToCart = t12;
  let t13;
  if ($[31] !== galleryImages || $[32] !== product.name || $[33] !== variantImageIndex) {
    t13 = <div><MediaGallery images={galleryImages} productName={product.name} activeIndex={variantImageIndex} /></div>;
    $[31] = galleryImages;
    $[32] = product.name;
    $[33] = variantImageIndex;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== product.name) {
    t14 = <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>;
    $[35] = product.name;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== displayPrice) {
    t15 = displayPrice && <span className="text-3xl font-bold text-gray-900">{displayPrice}</span>;
    $[37] = displayPrice;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== onSale || $[40] !== strikethroughPrice || $[41] !== t) {
    t16 = onSale && strikethroughPrice && <><span className="text-xl text-gray-500 line-through">{strikethroughPrice}</span><span className="bg-red-100 text-red-800 text-sm font-medium px-2.5 py-0.5 rounded">{t("sale")}</span></>;
    $[39] = onSale;
    $[40] = strikethroughPrice;
    $[41] = t;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15 || $[44] !== t16) {
    t17 = <div className="mt-4 flex items-center gap-4">{t15}{t16}</div>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== selectedVariant || $[47] !== t) {
    t18 = selectedVariant?.sku && <p className="mt-2 text-sm text-gray-500">{t("sku", {
        sku: selectedVariant.sku
      })}</p>;
    $[46] = selectedVariant;
    $[47] = t;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== inStock || $[50] !== t || $[51] !== unavailableInMarket) {
    t19 = !unavailableInMarket && <div className="mt-4">{inStock ? <span className="inline-flex items-center gap-1.5 text-green-600"><CircleCheckBig className="w-5 h-5" />{t("inStock")}</span> : <span className="inline-flex items-center gap-1.5 text-red-600"><CircleX className="w-5 h-5" />{t("outOfStock")}</span>}</div>;
    $[49] = inStock;
    $[50] = t;
    $[51] = unavailableInMarket;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== hasVariants || $[54] !== optionTypes || $[55] !== selectedVariant || $[56] !== variants) {
    t20 = hasVariants && optionTypes.length > 0 && <div className="mt-8"><VariantPicker variants={variants} optionTypes={optionTypes} selectedVariant={selectedVariant} onVariantChange={setSelectedVariant} /></div>;
    $[53] = hasVariants;
    $[54] = optionTypes;
    $[55] = selectedVariant;
    $[56] = variants;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== handleAddToCart || $[59] !== isPurchasable || $[60] !== loading || $[61] !== quantity || $[62] !== t || $[63] !== unavailableInMarket) {
    t21 = <div className="mt-8">{unavailableInMarket ? <p className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700">{t("unavailableInRegion")}</p> : <div className="flex gap-4"><QuantityPicker quantity={quantity} onDecrement={() => setQuantity(Math.max(1, quantity - 1))} onIncrement={() => setQuantity(quantity + 1)} size="lg" /><Button size="lg" onClick={handleAddToCart} disabled={loading || !isPurchasable}>{loading ? <><Loader2 className="animate-spin h-5 w-5" />{t("adding")}</> : isPurchasable ? <><ShoppingBag className="w-5 h-5" />{t("addToCart")}</> : t("outOfStock")}</Button></div>}</div>;
    $[58] = handleAddToCart;
    $[59] = isPurchasable;
    $[60] = loading;
    $[61] = quantity;
    $[62] = t;
    $[63] = unavailableInMarket;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== product.description || $[66] !== product.description_html) {
    t22 = <SpreeRichText html={product.description_html} text={product.description} className="mt-10 border-t pt-8 prose prose-sm max-w-none text-gray-600 prose-headings:mt-6 prose-headings:mb-2 prose-headings:text-base prose-headings:font-semibold prose-headings:text-gray-900 prose-li:my-1 [&_li>p]:my-0 [&>:first-child]:mt-0" />;
    $[65] = product.description;
    $[66] = product.description_html;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== product.custom_fields) {
    t23 = <ProductCustomFields customFields={product.custom_fields} />;
    $[68] = product.custom_fields;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== t14 || $[71] !== t17 || $[72] !== t18 || $[73] !== t19 || $[74] !== t20 || $[75] !== t21 || $[76] !== t22 || $[77] !== t23) {
    t24 = <div>{t14}{t17}{t18}{t19}{t20}{t21}{t22}{t23}</div>;
    $[70] = t14;
    $[71] = t17;
    $[72] = t18;
    $[73] = t19;
    $[74] = t20;
    $[75] = t21;
    $[76] = t22;
    $[77] = t23;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== t13 || $[80] !== t24) {
    t25 = <div className="container mx-auto px-4 sm:px-6 lg:px-8  py-8"><div className="grid grid-cols-1 lg:grid-cols-2 gap-12">{t13}{t24}</div></div>;
    $[79] = t13;
    $[80] = t24;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  return t25;
}
function _temp(v) {
  return v.purchasable;
}